import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, postMultipart, formatApiDetail } from "../../lib/api";
import { isLikelyPdfLibraryFile, isScopeLibraryFile, openStoredLibraryFile, portalFilePacketPath } from "../../lib/portalFiles";
import { useAuth } from "../../context/AuthContext";
import { StatusPill, STATUS_PILL_ROW_COMPACT_CLASS } from "../../components/StatusPill";
import { toast } from "sonner";
import { ExternalLink, Plus, Trash2, FileText, Image, Film, FileType, Send, ChevronDown, Upload, Check, EyeOff, Eye, Link2, Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { FullScreenModal } from "../../components/FullScreenModal";
import AdminSideDrawer, { DrawerFormBody } from "../../components/admin/AdminSideDrawer";
import { PortalLibraryRatioStat } from "../../components/PortalInventoryStat";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { cn } from "../../lib/utils";

const FOLDERS = [
  { key: "brand", label: "Brand Assets" },
  { key: "website", label: "Website / Digital" },
  { key: "operations", label: "Operations & Systems" },
  { key: "presentations", label: "Presentations & Decks" },
  { key: "contracts", label: "Contracts & Invoices" },
];

const FOLDER_KEYS = new Set(FOLDERS.map((folder) => folder.key));

const fileFolderKey = (file) => {
  const raw = typeof file?.folder === "string" ? file.folder.trim() : "";
  if (!raw) return "brand";
  const key = raw.toLowerCase();
  const byKey = FOLDERS.find((folder) => folder.key === key);
  if (byKey) return byKey.key;
  const byLabel = FOLDERS.find((folder) => folder.label.toLowerCase() === raw.toLowerCase());
  return byLabel?.key || "brand";
};

const TYPE_ICON = { figma: FileType, doc: FileText, presentation: FileText, image: Image, video: Film, pdf: FileText, link: ExternalLink };

const MONTH_SHORT = {
  january: "jan",
  jan: "jan",
  february: "feb",
  feb: "feb",
  march: "mar",
  mar: "mar",
  april: "apr",
  apr: "apr",
  may: "may",
  june: "jun",
  jun: "jun",
  july: "jul",
  jul: "jul",
  august: "aug",
  aug: "aug",
  september: "sep",
  sept: "sep",
  sep: "sep",
  october: "oct",
  oct: "oct",
  november: "nov",
  nov: "nov",
  december: "dec",
  dec: "dec",
};

const NEXT_MONTH = {
  jan: "feb",
  feb: "mar",
  mar: "apr",
  apr: "may",
  may: "jun",
  jun: "jul",
  jul: "aug",
  aug: "sep",
  sep: "oct",
  oct: "nov",
  nov: "dec",
  dec: "jan",
};

function monthKey(word) {
  const w = String(word || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "");
  return MONTH_SHORT[w] || MONTH_SHORT[w.slice(0, 3)] || null;
}

/** Scope packet subtitle: «aug to sep scope + invoice». */
function formatScopeLibraryNote(note) {
  const raw = String(note || "").trim();
  if (!raw) return "";

  const canonical = raw.match(
    /^([A-Za-z]+)\s*(?:[–—-]|to)\s*([A-Za-z]+)(?:\s+scope(?:\s*\+\s*invoice)?)?$/i,
  );
  if (canonical) {
    const a = monthKey(canonical[1]);
    const b = monthKey(canonical[2]);
    if (a && b) return `${a} to ${b} scope + invoice`;
  }

  const withDays = raw.match(
    /^([A-Za-z]+)\s+\d{1,2}(?:st|nd|rd|th)?\s*(?:[–—-]|to)\s*([A-Za-z]+)\s+\d{1,2}(?:st|nd|rd|th)?(?:,?\s*20\d{2})?$/i,
  );
  if (withDays) {
    const a = monthKey(withDays[1]);
    const b = monthKey(withDays[2]);
    if (a && b) return `${a} to ${b} scope + invoice`;
  }

  const single = raw.match(
    /^([A-Za-z]+)\s+20\d{2}$/i,
  );
  if (single) {
    const a = monthKey(single[1]);
    if (a) return `${a} to ${NEXT_MONTH[a]} scope + invoice`;
  }

  return raw;
}

function libraryFileDisplayStatus(file) {
  if (file?.status === "paid") return "paid";
  // Past / completed scope packets used to land as «final» — show PAID.
  if (file?.status === "final" && isScopeLibraryFile(file)) return "paid";
  return file?.status === "approved" ? "final" : (file?.status || "draft");
}

function libraryFileSubtitle(file) {
  const note = String(file?.note || "").trim();
  if (!note) return "";
  if (isScopeLibraryFile(file) || fileFolderKey(file) === "contracts") {
    return formatScopeLibraryNote(note);
  }
  return note;
}

const LibraryFileStatusMenu = ({ file, linkedApproval, busy, onRequestApproval }) => {
  const [open, setOpen] = useState(false);
  const displayStatus = libraryFileDisplayStatus(file);
  const approvalStatus = linkedApproval?.status;
  const pendingApproval = approvalStatus === "pending";
  const revisionRequested = approvalStatus === "revision_requested";

  if (displayStatus === "paid") {
    return (
      <StatusPill
        status="paid"
        label="PAID"
        className={STATUS_PILL_ROW_COMPACT_CLASS}
        data-testid={`library-status-paid-${file.id}`}
      />
    );
  }

  const canRequest =
    !file?.studio_only
    && (displayStatus === "final" || displayStatus === "draft")
    && !pendingApproval
    && !revisionRequested;

  if (!canRequest) {
    const status = revisionRequested ? "revision_requested" : (pendingApproval ? "for_review" : displayStatus);
    const label = revisionRequested ? "REVISION REQUESTED" : (pendingApproval ? "PENDING APPROVAL" : undefined);
    return <StatusPill status={status} label={label} className={STATUS_PILL_ROW_COMPACT_CLASS} />;
  }

  const handleRequest = async () => {
    setOpen(false);
    await onRequestApproval(file);
  };

  const pillTone = displayStatus === "final" ? "t-pill-accent" : "t-pill-meta";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "t-pill inline-flex shrink-0 cursor-pointer border bg-transparent text-left font-[inherit] uppercase hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
            pillTone,
            STATUS_PILL_ROW_COMPACT_CLASS
          )}
          data-testid={`library-status-trigger-${file.id}`}
          aria-label="File status options"
          disabled={busy}
        >
          {displayStatus.replace(/_/g, " ")}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid={`library-status-menu-${file.id}`}
      >
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
            onClick={handleRequest}
            data-testid={`library-request-approval-${file.id}`}
          >
            Request approval
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const extractGoogleId = (url) => {
  if (!url) return null;
  const s = String(url);
  // drive file links: /file/d/<id>/...
  let m = s.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  if (m?.[1]) return m[1];
  // drive open links: open?id=<id>
  m = s.match(/[?&]id=([^&]+)/i);
  if (m?.[1]) return m[1];
  // docs links: /document/d/<id>/...
  m = s.match(/docs\.google\.com\/document\/d\/([^/]+)/i);
  if (m?.[1]) return m[1];
  return null;
};

const normalizedOpenUrl = (f) => {
  const url = (f?.url || "").trim();
  if (!url) return url;
  const id = extractGoogleId(url);
  if (!id) return url;

  // Prefer direct viewers/editors rather than the Drive file page.
  if (f.file_type === "pdf") {
    return `https://drive.google.com/file/d/${id}/preview`;
  }
  if (f.file_type === "doc") {
    return `https://docs.google.com/document/d/${id}/edit`;
  }
  return url;
};

export default function FilesPage({ embedded = false }) {
  const { client, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const folderParam = searchParams.get("folder");
  const activeFolder = FOLDER_KEYS.has(folderParam) ? folderParam : "brand";
  const [showAdd, setShowAdd] = useState(false);
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
  const [pendingShareFile, setPendingShareFile] = useState(null);
  const [shareBusy, setShareBusy] = useState(null); // "copy" | "email" | null
  const [draggingFileId, setDraggingFileId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [replacingFileId, setReplacingFileId] = useState(null);
  const [requestingApprovalFileId, setRequestingApprovalFileId] = useState(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const params = isAdmin ? `?client_id=${client?.id}` : "";

  const load = async () => {
    if (!client) return;
    const requests = [api.get(`/portal/files${params}`)];
    if (isAdmin) requests.push(api.get(`/portal/approvals${params}`));
    const [filesRes, approvalsRes] = await Promise.all(requests);
    setFiles(filesRes.data);
    setApprovals(isAdmin ? (approvalsRes?.data || []) : []);
  };

  useEffect(() => { load(); }, [client, isAdmin]);

  const approvalByFileId = useMemo(() => {
    const map = new Map();
    approvals.forEach((approval) => {
      if (approval?.file_id) map.set(approval.file_id, approval);
    });
    return map;
  }, [approvals]);

  const startReplace = (file) => {
    replaceTargetRef.current = file;
    replaceInputRef.current?.click();
  };

  const handleReplaceSelected = async (e) => {
    const upload = e.target.files?.[0];
    const target = replaceTargetRef.current;
    e.target.value = "";
    replaceTargetRef.current = null;
    if (!upload || !target) return;
    setReplacingFileId(target.id);
    try {
      const fd = new FormData();
      fd.append("file", upload);
      await postMultipart(`/portal/files/${target.id}/replace${params}`, fd);
      toast.success("Revision uploaded for client review");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not upload revision");
    } finally {
      setReplacingFileId(null);
    }
  };

  const confirmRemoveFile = async () => {
    if (!pendingDeleteFile) return;
    const id = pendingDeleteFile.id;
    setPendingDeleteFile(null);
    try {
      await api.delete(`/portal/files/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove file");
    }
  };

  const approveFile = async (approval) => {
    if (!approval) return;
    try {
      await api.post(`/portal/approvals/${approval.id}/action`, { action: "approve" });
      toast.success("Marked approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    }
  };

  const sendToClient = (f) => {
    setPendingShareFile(f);
  };

  const prepareShareLink = async (f) => {
    const { data } = await api.post(`/portal/files/${f.id}/prepare-share${params}`);
    const url = (data?.url || "").trim();
    if (!url) throw new Error("No share link returned");
    return url;
  };

  const localScopeShareUrl = (f) => {
    const path = portalFilePacketPath(f, client);
    if (!path) return "";
    return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const copyShareLink = async () => {
    if (!pendingShareFile || shareBusy) return;
    setShareBusy("copy");
    try {
      let url = localScopeShareUrl(pendingShareFile);
      try {
        url = await prepareShareLink(pendingShareFile);
      } catch {
        // Pinning preferred packet is best-effort; still copy the in-app scope URL.
        if (!url) throw new Error("No share link available");
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Scope link copied — client can open it in-app");
      } catch {
        toast.message(url);
      }
      setPendingShareFile(null);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail || e.message, "Could not copy link"));
    } finally {
      setShareBusy(null);
    }
  };

  const emailShareLink = async () => {
    if (!pendingShareFile || shareBusy) return;
    setShareBusy("email");
    try {
      const isScope = isScopeLibraryFile(pendingShareFile);
      const { data } = await api.post(`/portal/files/${pendingShareFile.id}/send${params}`, {
        mode: isScope ? "link" : "pdf",
      });
      toast.success(
        data?.to_email
          ? isScope
            ? `Scope link emailed to ${data.to_email}`
            : `Sent to ${data.to_email}`
          : isScope
            ? "Scope link emailed"
            : "Sent to client",
      );
      setPendingShareFile(null);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail || e.message, "Could not send"));
    } finally {
      setShareBusy(null);
    }
  };

  const requestApproval = async (f) => {
    setRequestingApprovalFileId(f.id);
    try {
      const { data } = await api.post(`/portal/files/${f.id}/request-approval${params}`);
      if (data?.already_pending) {
        toast.message("Already sent for client approval");
      } else {
        toast.success("Sent for client approval");
      }
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not request approval"));
      throw err;
    } finally {
      setRequestingApprovalFileId(null);
    }
  };

  const setActiveFolder = (folderKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("folder", folderKey);
      return next;
    }, { replace: true });
  };

  const moveFile = async (f, nextFolder) => {
    if (fileFolderKey(f) === nextFolder) return;
    try {
      await api.patch(`/portal/files/${f.id}${params}`, { folder: nextFolder });
      const label = FOLDERS.find((folder) => folder.key === nextFolder)?.label || "folder";
      toast.success(`Moved to ${label}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update folder");
    }
  };

  const toggleStudioOnly = async (f) => {
    const next = !f.studio_only;
    try {
      await api.patch(`/portal/files/${f.id}${params}`, { studio_only: next });
      toast.success(next ? "Studio only — client cannot see this" : "Visible to share with client");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update visibility");
    }
  };

  const handleFileDragStart = (e, file) => {
    if (!isAdmin) return;
    setDraggingFileId(file.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", file.id);
  };

  const handleFileDragEnd = () => {
    setDraggingFileId(null);
    setDragOverFolder(null);
  };

  const handleFolderDragOver = (e) => {
    if (!isAdmin || !draggingFileId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleFolderDrop = (e, folderKey) => {
    e.preventDefault();
    if (!isAdmin) return;
    const fileId = e.dataTransfer.getData("text/plain") || draggingFileId;
    const file = files.find((item) => item.id === fileId);
    setDragOverFolder(null);
    setDraggingFileId(null);
    if (file) moveFile(file, folderKey);
  };

  const openFile = async (f) => {
    // Stored library PDFs open in-app (viewer + Download). Other types may download.
    if (f.is_uploaded || f.storage_path || (f.url || "").startsWith("/api/")) {
      const preferInApp = isLikelyPdfLibraryFile(f) || isScopeLibraryFile(f);
      const ok = await openStoredLibraryFile(
        f,
        client,
        (fileId) => api.get(`/portal/files/item/${fileId}/download`, { responseType: "blob" }),
        { forceDownload: !preferInApp && !isAdmin },
      );
      if (!ok) toast.error("Could not open file");
      return;
    }
    window.open(normalizedOpenUrl(f), "_blank", "noreferrer");
  };

  if (!client) return <div className="p-12 t-meta">No client context.</div>;

  const filtered = files.filter((f) => fileFolderKey(f) === activeFolder);

  return (
    <div className={embedded ? "t-fade-up min-w-0" : "p-4 sm:p-5 md:p-7 t-fade-up min-w-0"} data-testid="files-page">
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-10">
          <div>
            {isAdmin ? (
              <>
                <div className="label-eyebrow mb-2">Library</div>
                <h1 className="font-heading text-2xl tracking-tight !font-extralight md:text-3xl">
                  Files & packets
                </h1>
              </>
            ) : (
              <h1 className="m-0 font-[inherit]">
                <PortalLibraryRatioStat folderCount={filtered.length} totalCount={files.length} embedded={false} />
              </h1>
            )}
          </div>
          {isAdmin && (
            <button type="button" onClick={() => setShowAdd(true)} className="neo-cta self-start" data-testid="add-file-btn">
              <Plus size={16} strokeWidth={1.4} /> Add file
            </button>
          )}
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-6">
          <PortalLibraryRatioStat folderCount={filtered.length} totalCount={files.length} embedded />
          {isAdmin && (
            <button type="button" onClick={() => setShowAdd(true)} className="neo-cta shrink-0" data-testid="add-file-btn">
              <Plus size={16} strokeWidth={1.4} /> Add file
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,260px)_1fr] gap-6 md:gap-8">
        {/* Folder list */}
        <aside className="neo-panel overflow-hidden min-w-0" data-testid="folder-nav">
          {FOLDERS.map((f) => {
            const count = files.filter((x) => fileFolderKey(x) === f.key).length;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFolder(f.key)}
                onDragOver={handleFolderDragOver}
                onDragEnter={() => isAdmin && draggingFileId && setDragOverFolder(f.key)}
                onDragLeave={() => setDragOverFolder((current) => (current === f.key ? null : current))}
                onDrop={(e) => handleFolderDrop(e, f.key)}
                className={`block w-full min-h-[44px] border-b border-black/5 px-5 py-4 text-left last:border-b-0 t-row-hover ${activeFolder === f.key ? "bg-[rgba(0,30,255,0.06)]" : ""} ${dragOverFolder === f.key ? "ring-2 ring-inset ring-[var(--t-accent)]" : ""}`}
                data-testid={`folder-${f.key}`}
              >
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${activeFolder === f.key ? "" : "text-[var(--t-meta)]"}`}>{f.label}</span>
                  <span className="t-meta text-xs">{count}</span>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Files grid */}
        <div className="space-y-3" data-testid="files-list">
          {filtered.length === 0 ? (
            <div className="neo-panel text-center py-16 t-meta">No files in this folder yet.</div>
          ) : filtered.map((f) => {
            const Icon = TYPE_ICON[f.file_type] || ExternalLink;
            const studioOnly = Boolean(f.studio_only);
            const canSend = isAdmin
              && !studioOnly
              && fileFolderKey(f) === "contracts"
              && (Boolean(f.is_uploaded || f.storage_path) || Boolean((f.url || "").trim()));
            const linkedApproval = approvalByFileId.get(f.id);
            const canApprove = isAdmin && !studioOnly && linkedApproval?.status === "pending";
            const canReplaceRevision = isAdmin
              && !studioOnly
              && (f.is_uploaded || f.storage_path)
              && linkedApproval
              && (linkedApproval.status === "revision_requested" || linkedApproval.status === "pending");
            return (
              <div
                key={f.id}
                draggable={isAdmin}
                onDragStart={(e) => handleFileDragStart(e, f)}
                onDragEnd={handleFileDragEnd}
                className={`neo-panel p-5 t-row-hover relative ${draggingFileId === f.id ? "opacity-50" : ""} ${isAdmin ? "cursor-grab active:cursor-grabbing" : ""}`}
                data-testid={`file-${f.id}`}
              >
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="min-w-0 flex-1">
                      <button type="button" onClick={() => openFile(f)} className="font-heading !font-light text-base tracking-tight hover:text-[var(--t-accent)] transition-colors flex items-center gap-2 text-left" data-testid={`open-file-${f.id}`}>
                        {f.name} <ExternalLink size={12} strokeWidth={1.4} />
                      </button>
                      {f.note && <div className="t-meta mt-1">{libraryFileSubtitle(f)}</div>}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {isAdmin ? (
                          <LibraryFileStatusMenu
                            file={f}
                            linkedApproval={linkedApproval}
                            busy={requestingApprovalFileId === f.id}
                            onRequestApproval={requestApproval}
                          />
                        ) : (
                          <StatusPill
                            status={libraryFileDisplayStatus(f)}
                            label={libraryFileDisplayStatus(f) === "paid" ? "PAID" : undefined}
                            className={STATUS_PILL_ROW_COMPACT_CLASS}
                          />
                        )}
                        {studioOnly && (
                          <span className="t-pill t-pill-meta inline-flex items-center gap-1" data-testid={`studio-only-badge-${f.id}`}>
                            <EyeOff size={10} strokeWidth={1.6} aria-hidden /> Studio only
                          </span>
                        )}
                        <span className="t-pill t-pill-meta">{f.file_type}</span>
                        <span className="t-pill t-pill-meta">{f.version}</span>
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <>
                      {canSend && (
                        <button
                          type="button"
                          onClick={() => sendToClient(f)}
                          className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)] absolute top-4 right-3"
                          data-testid={`send-file-${f.id}`}
                          aria-label="Send to client"
                        >
                          <Send size={14} strokeWidth={1.4} />
                        </button>
                      )}
                      <div className="absolute bottom-4 right-3 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleStudioOnly(f)}
                          className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                          data-testid={`toggle-studio-only-${f.id}`}
                          aria-label={studioOnly ? "Allow sharing with client" : "Mark studio only"}
                          title={studioOnly ? "Allow sharing with client" : "Mark studio only"}
                        >
                          {studioOnly ? <Eye size={14} strokeWidth={1.4} /> : <EyeOff size={14} strokeWidth={1.4} />}
                        </button>
                        {canApprove && (
                          <button
                            type="button"
                            onClick={() => approveFile(linkedApproval)}
                            className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                            data-testid={`approve-file-${f.id}`}
                            aria-label="Mark approved"
                          >
                            <Check size={14} strokeWidth={1.4} />
                          </button>
                        )}
                        {canReplaceRevision && (
                          <button
                            type="button"
                            onClick={() => startReplace(f)}
                            disabled={replacingFileId === f.id}
                            className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-accent)] disabled:opacity-50"
                            data-testid={`replace-file-${f.id}`}
                            aria-label="Upload revised file"
                          >
                            <Upload size={14} strokeWidth={1.4} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setPendingDeleteFile(f)}
                          className="t-btn-ghost text-[var(--t-meta)] hover:text-[var(--t-error)]"
                          data-testid={`delete-file-${f.id}`}
                          aria-label="Delete file"
                        >
                          <Trash2 size={14} strokeWidth={1.4} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAdd && <AddFileModal key={`${client.id}-${activeFolder}`} folder={activeFolder} clientId={client.id} onClose={() => setShowAdd(false)} onSaved={load} />}

      {pendingShareFile && (
        <ShareFileModal
          file={pendingShareFile}
          busy={shareBusy}
          onClose={() => !shareBusy && setPendingShareFile(null)}
          onCopyLink={copyShareLink}
          onEmail={emailShareLink}
        />
      )}

      <input
        ref={replaceInputRef}
        type="file"
        className="sr-only"
        onChange={handleReplaceSelected}
        data-testid="replace-file-input"
      />

      <AlertDialog open={pendingDeleteFile !== null} onOpenChange={(open) => !open && setPendingDeleteFile(null)}>
        <AlertDialogContent
          className="z-[80] max-w-md border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)] gap-5"
          data-testid="library-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
              Delete this file?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
              {pendingDeleteFile ? (
                <>
                  <span className="text-[var(--t-text)]">&ldquo;{pendingDeleteFile.name}&rdquo;</span> will be removed from the library. You cannot undo this.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:justify-end">
            <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
              Cancel
            </AlertDialogCancel>
            <button type="button" className="neo-cta" data-testid="library-delete-confirm-btn" onClick={confirmRemoveFile}>
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const stripExtension = (filename) => {
  if (!filename) return "";
  const lastDot = filename.lastIndexOf(".");
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
};

const ShareFileModal = ({ file, busy, onClose, onCopyLink, onEmail }) => {
  const isScope = isScopeLibraryFile(file);
  return (
    <AdminSideDrawer
      onClose={onClose}
      eyebrow="Share"
      title={isScope ? "Send scope" : "Send to client"}
      testId="share-file-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="t-btn-secondary !rounded-xl"
            disabled={Boolean(busy)}
          >
            Cancel
          </button>
          {isScope ? (
            <button
              type="button"
              className="neo-cta"
              onClick={onCopyLink}
              disabled={Boolean(busy)}
              data-testid="share-file-copy-link"
            >
              <Link2 size={16} strokeWidth={1.4} />
              {busy === "copy" ? "Copying…" : "Copy link"}
            </button>
          ) : (
            <button
              type="button"
              className="neo-cta"
              onClick={onEmail}
              disabled={Boolean(busy)}
              data-testid="share-file-email"
            >
              <Mail size={16} strokeWidth={1.4} />
              {busy === "email" ? "Sending…" : "Email PDF"}
            </button>
          )}
        </>
      }
    >
      <DrawerFormBody>
        <div>
          <div className="label-eyebrow mb-2 block">File</div>
          <p className="text-sm text-[var(--t-text)] min-w-0 truncate" title={file?.name}>
            {file?.name}
          </p>
        </div>
        {isScope ? (
          <>
            <p className="t-meta text-xs">
              Opens the scope in-app at their workspace URL — no PDF attachment.
            </p>
            <button
              type="button"
              className="t-btn-secondary !rounded-xl w-full justify-center min-h-[44px] inline-flex items-center gap-2"
              onClick={onEmail}
              disabled={Boolean(busy)}
              data-testid="share-file-email"
            >
              <Mail size={16} strokeWidth={1.4} />
              {busy === "email" ? "Sending…" : "Email link"}
            </button>
          </>
        ) : null}
      </DrawerFormBody>
    </AdminSideDrawer>
  );
};

const AddFileModal = ({ folder, clientId, onClose, onSaved }) => {
  const [mode, setMode] = useState("upload");
  const [form, setForm] = useState({
    folder,
    name: "",
    url: "",
    file_type: "link",
    version: "v1",
    status: "draft",
    note: "",
    studio_only: true,
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm((prev) => (prev.folder === folder ? prev : { ...prev, folder }));
  }, [folder]);

  const selectUploadFile = (file) => {
    setUploadFile(file || null);
    if (file && !form.name.trim()) {
      setForm((prev) => ({ ...prev, name: stripExtension(file.name) }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "upload" && !uploadFile) {
      toast.error("Choose a file to upload");
      return;
    }
    const status = form.studio_only && form.status === "for_review" ? "draft" : form.status;
    setBusy(true);
    try {
      if (mode === "upload") {
        const fd = new FormData();
        fd.append("file", uploadFile);
        fd.append("folder", form.folder);
        if (form.name.trim()) fd.append("name", form.name.trim());
        fd.append("version", form.version);
        fd.append("status", status);
        fd.append("studio_only", form.studio_only ? "true" : "false");
        if (form.note.trim()) fd.append("note", form.note.trim());
        await postMultipart(`/portal/files/upload?client_id=${clientId}`, fd);
      } else {
        await api.post(`/portal/files?client_id=${clientId}`, { ...form, status });
      }
      toast.success(mode === "upload" ? "File uploaded" : "File added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || (mode === "upload" ? "Could not upload file" : "Could not add file"));
    } finally { setBusy(false); }
  };

  return (
    <FullScreenModal
      onClose={onClose}
      ariaLabel="New file"
      testid="add-file-modal"
      contentClassName="max-h-[90vh] overflow-y-auto"
    >
      <div className="label-eyebrow mb-3">Add</div>
      <h2 className="font-heading text-2xl mb-6 tracking-tight">New file</h2>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Source</label>
            <div className="grid grid-cols-2 gap-2" data-testid="file-mode-toggle">
              <button
                type="button"
                className={`min-h-[44px] rounded-[var(--t-event-pill-radius)] border px-4 text-sm transition-colors ${mode === "upload" ? "border-[var(--t-accent)] bg-white/50 text-[var(--t-text)]" : "border-[var(--t-border)] text-[var(--t-meta)] hover:text-[var(--t-text)]"}`}
                onClick={() => setMode("upload")}
                data-testid="file-mode-upload"
              >
                Upload
              </button>
              <button
                type="button"
                className={`min-h-[44px] rounded-[var(--t-event-pill-radius)] border px-4 text-sm transition-colors ${mode === "link" ? "border-[var(--t-accent)] bg-white/50 text-[var(--t-text)]" : "border-[var(--t-border)] text-[var(--t-meta)] hover:text-[var(--t-text)]"}`}
                onClick={() => setMode("link")}
                data-testid="file-mode-link"
              >
                External link
              </button>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Folder</label>
            <div className="relative">
              <select className="t-select !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full appearance-none !px-4 !pr-10" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })}>
                {FOLDERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <ChevronDown size={16} strokeWidth={1.6} aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 opacity-70 text-[var(--t-meta)]" />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Name</label>
            <input className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required={mode === "link"} data-testid="file-name-input" />
          </div>
          {mode === "upload" ? (
            <div>
              <label className="label-eyebrow block mb-2">File</label>
              <label className={`block w-full ${busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
                <input
                  type="file"
                  className="sr-only"
                  onChange={(e) => selectUploadFile(e.target.files?.[0] || null)}
                  disabled={busy}
                  data-testid="file-upload-input"
                />
                <span className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full min-h-[44px] inline-flex items-center justify-between gap-3 !px-4 text-left">
                  <span className={`truncate text-sm ${uploadFile ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"}`}>
                    {uploadFile ? uploadFile.name : "Choose file"}
                  </span>
                  <span className="shrink-0 text-xs uppercase tracking-wide text-[var(--t-meta)]">
                    {uploadFile ? "Replace" : "Browse"}
                  </span>
                </span>
              </label>
            </div>
          ) : (
            <div>
              <label className="label-eyebrow block mb-2">External URL</label>
              <input type="url" className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required data-testid="file-url-input" />
            </div>
          )}
          <div className={`grid grid-cols-1 gap-4 ${mode === "link" ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            {mode === "link" && (
              <div>
                <label className="label-eyebrow block mb-2">Type</label>
                <div className="relative">
                  <select className="t-select !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full appearance-none !px-4 !pr-10" value={form.file_type} onChange={(e) => setForm({ ...form, file_type: e.target.value })}>
                    <option value="link">Link</option><option value="doc">Doc</option><option value="presentation">Deck</option>
                    <option value="figma">Figma</option><option value="image">Image</option><option value="video">Video</option><option value="pdf">PDF</option>
                  </select>
                  <ChevronDown size={16} strokeWidth={1.6} aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 opacity-70 text-[var(--t-meta)]" />
                </div>
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-2">Version</label>
              <input className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Status</label>
              <div className="relative">
                <select
                  className="t-select !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full appearance-none !px-4 !pr-10"
                  value={form.studio_only && form.status === "for_review" ? "draft" : form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  {!form.studio_only && <option value="for_review">For review</option>}
                  <option value="final">Final</option>
                </select>
                <ChevronDown size={16} strokeWidth={1.6} aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 opacity-70 text-[var(--t-meta)]" />
              </div>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Visibility</label>
            <label
              className="neo-panel flex items-start gap-3 px-4 py-3 cursor-pointer"
              data-testid="file-studio-only-toggle"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(form.studio_only)}
                onChange={(e) => {
                  const studio_only = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    studio_only,
                    status: studio_only && prev.status === "for_review" ? "draft" : prev.status,
                  }));
                }}
              />
              <span className="min-w-0">
                <span className="block text-sm text-[var(--t-text)]">Studio only</span>
                <span className="t-meta text-xs block mt-0.5">
                  Hidden from the client. Uncheck when you want to send or request approval.
                </span>
              </span>
            </label>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Note (optional)</label>
            <input className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] flex-1">Cancel</button>
            <button type="submit" className="neo-cta flex-1 justify-center" disabled={busy || (mode === "upload" && !uploadFile)} data-testid="file-submit-btn">
              {busy ? (mode === "upload" ? "Uploading…" : "Saving…") : (mode === "upload" ? "Upload" : "Save")}
            </button>
          </div>
        </form>
    </FullScreenModal>
  );
};
